import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import { PhotoViewer } from "./PhotoViewer";
import { BackupMenu } from "./BackupMenu";
// THE RULE — the restructured home (ascent pyramid, log rail, session ladder, sessions).
// See therule.tsx for the a3 restructure notes R1–R6.
import { TopBand, Pyramid, ProjectBoard, Sessions, LogRail, FirstRun, readScale, type Scale } from "./therule";
import {
  store, computeStreak, streakCopy, corpusStats,
  DEFAULT_STYLES, ASCENTS, DISCIPLINES, ENVIRONMENTS,
  ASCENT_LABELS, DISCIPLINE_LABELS, ENVIRONMENT_LABELS, isSendAscent,
  normalizeTags, normalizeTag, todayLocal,
  type Climb, type NewClimb, type SeasonSignals,
  type Ascent, type Discipline, type Environment,
} from "./store";
import { recapSeason, askTicks, isCoachingOrMedical, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import {
  Sparkles, Mountain, Trash2, X, Lock, WifiOff, RefreshCw,
  CheckCircle2, CircleDashed, Flag, MapPin, Send,
} from "lucide-react";

const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "";
const LIST_CAP = 50000;

/**
 * a line up to the top-out — the shape of a logged climb.
 * The last of the shared lucide `Sparkles` launcher glyphs — 20 of the fleet's 26 chat launchers
 * rendered it in common, which is the shared-fingerprint half of Guideline 4.3(a). This app's
 * OUTLINE was already its own, so only the glyph needed to move.
 */
function RouteMark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      {/* A line up to the top-out. The carabiner drawn first did not survive 16px — the gate that
          makes it a carabiner vanished and it read as a plain oval. A route reads at any size. */}
      <path d="M4.6 19.6 8.4 13.2l3.1 3.3 4.4-8.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="17.7" cy="5.3" r="2" fill="currentColor" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out. (mirrors expense_audit_ai)
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches a deleted account).
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-send-log://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-send-log://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented) {
    return <AiConsent brand="Send Log"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed) return <Login />;
  return <Home email={email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ color: "var(--accent)", marginBottom: "0.4rem", display: "flex", justifyContent: "center" }}><Mountain size={34} /></div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Send Log</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your tick list lives on this
          device; the season recap runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-send-log");
              else if (p === "macos") await signInMac("oriora-send-log");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/oriora-send-log/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

type Tab = "log" | "insights";

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [climbs, setClimbs] = useState<Climb[]>([]);
  const [tab, setTab] = useState<Tab>("log");
  const [editing, setEditing] = useState<Climb | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Climb | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not
  // change any existing screen. A chat-driven change reloads the tick list via `reload`.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const exportRef = useRef<(() => void) | null>(null);

  // Signals carry the PROJECT lines the board needs — attempts, sessions, sent, last_touched —
  // all computed on-device already (signals.rs::ProjectStat). The home screen was not reading them.
  const [homeSignals, setHomeSignals] = useState<SeasonSignals | null>(null);

  const reload = useCallback(async () => {
    setClimbs(await store.listClimbs({ limit: LIST_CAP }));
    try { setHomeSignals(await store.computeSignals(todayLocal())); } catch { setHomeSignals(null); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): Log / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "log": setEditing(null); setShowLog(true); break;
        case "recap": setTab("insights"); break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // NOTE: computeStreak/corpusStats are no longer read by the home. The streak hero is DELETED
  // (restructure R1) — the readout is now TopBand over a rolling 12 months. corpusStats stays
  // in use by the recap surface.
  const stats = useMemo(() => corpusStats(climbs), [climbs]);
  // Which scale the pyramid is showing. Boulder vs Rope (sport/trad/toprope) are different
  // ladders and must not share one pyramid.
  const [grp, setGrp] = useState<"boulder" | "rope">("boulder");
  // Grade preset handed to the log sheet by the rail.
  const [preset, setPreset] = useState<string | undefined>(undefined);
  // Which grade scale the rail shows — asked once on first run, changeable by logging any grade.
  const [scale, setScale] = useState<Scale>(() => readScale());

  const doExport = useCallback(() => {
    if (climbs.length === 0) return;
    const { csv, filename } = buildExport(climbs);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [climbs]);
  exportRef.current = doExport;

  function startLog(climb?: Climb) { setEditing(climb ?? null); setPreset(undefined); setShowLog(true); }
  /** From the LOG RAIL — opens the sheet with that band's grade already filled. */
  function startLogAt(grade?: string) { setEditing(null); setPreset(grade); setShowLog(true); }
  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteClimb(id);
    await reload();
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      // Bottom padding also has to clear the fixed LOG RAIL (~92px) — otherwise the last session
      // row sits under it and is unreachable, which is precisely the class of wiring defect P6c
      // exists to catch.
      padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) calc(112px + env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="banner banner--offline" style={{ marginBottom: "var(--sp-4)" }}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          {/* verbatim copy fence — do not reword */}
          Season recap paused — your climbs still save locally
        </div>
      )}

      {/* HEADER — no serif app-name display type. The fleet's shared header is one of the four
          things the sibling test flagged (R6); this is a graduation label, not a title block. */}
      <header className="row" style={{ justifyContent: "space-between", marginBottom: "var(--sp-4)" }}>
        <div className="row" style={{ gap: "0.5rem" }}>
          <span aria-hidden="true" style={{ display: "inline-block", width: 14, height: 2, background: "var(--accent)" }} />
          <span className="lbl" style={{ color: "var(--ink-1)", fontSize: "var(--fs-sm)", letterSpacing: "0.16em" }}>Send Log</span>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <button className="btn-ghost lbl" onClick={() => setTab(tab === "log" ? "insights" : "log")}
                  aria-label={tab === "log" ? "Open the season recap" : "Back to the rule"}>
            {tab === "log" ? "Season" : "The rule"}
          </button>
          <BackupMenu
            appName="Send Log" fileSlug="send-log" appScheme="oriora-send-log"
            hasData={climbs.length > 0} onRestored={reload}
            onExportCsv={climbs.length > 0 ? doExport : undefined}
          />
          <AccountMenu email={email} appSlug="oriora-send-log" appName="Oriora Send Log" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {tab === "log" ? (
        climbs.length === 0 ? (
          /* FIRST RUN — art-directed, and the screen App Review sees first. */
          <FirstRun onPickScale={setScale} />
        ) : (
        <>
          {/* R1 — the readout replaces the streak hero */}
          <TopBand climbs={climbs} />

          {/* Boulder and rope are different ladders; they must not share one pyramid. */}
          <div className="row" style={{ gap: 0, margin: "var(--sp-4) 0 0" }}>
            <button className={`seg${grp === "boulder" ? " selected" : ""}`} onClick={() => setGrp("boulder")}>Boulder</button>
            <button className={`seg${grp === "rope" ? " selected" : ""}`} onClick={() => setGrp("rope")}>Rope</button>
          </div>

          {/* R1 — THE ASCENT PYRAMID leads the home */}
          <Pyramid climbs={climbs} group={grp} />

          {/* R5 — projects as a real surface with the session ladder */}
          <ProjectBoard signals={homeSignals} onLog={() => startLog()} />

          {/* R3 — history re-cut by session, demoted below the pyramid and the projects */}
          <Sessions climbs={climbs} onOpen={startLog} />
        </>
        )
      ) : (
        <InsightsTab climbs={climbs} />
      )}

      {/* R2 — THE LOG RAIL. The app's own verb, pinned and always reachable. Only on the rule
          surface; the recap is a reading surface and should not carry a logging affordance. */}
      {tab === "log" && <LogRail onLog={startLogAt} scale={scale} firstRun={climbs.length === 0} />}

      {showLog && (
        <LogClimb editing={editing} preset={preset}
                  onClose={() => { setShowLog(false); setEditing(null); setPreset(undefined); }}
                  onSaved={() => { setShowLog(false); setEditing(null); setPreset(undefined); void reload(); }} />
      )}

      {confirmDelete && (
        <ModalShell title="Delete climb?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
            Delete your {fmtDate(confirmDelete.climbed_on)} {confirmDelete.grade || "climb"}{confirmDelete.venue ? ` at ${confirmDelete.venue}` : ""}? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the list. */}
      {!chatOpen && (
        <button className="sdl-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <RouteMark size={16} /> Chat
        </button>
      )}
      <aside className={`sdl-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="sdl-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to log, find, or recap</span>
          <button className="sdl-close" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

/** THE PROJECT BOARD — the app's own primitive (ledger row 2b).
 *
 *  A climber does not work "climbs", they work a PROJECT: one route or problem returned to across
 *  sessions until it goes. signals.rs already derives exactly that per project — total_attempts,
 *  sessions, sent, last_touched — and the home screen led with the shared Streak hero instead
 *  (literally the component row 2's grep flags), burying the projects in a stats panel on the
 *  other tab. So the screen answered "how many days in a row have I logged" when the question a
 *  climber opens the app with is "what am I working, and is it going?".
 *
 *  Open projects lead, ordered by work invested. Sent ones move to their own line — the tick is
 *  the point, but once it has gone it stops being a decision.
 *
 *  🔒 Descriptive: attempts and sessions are what the climber logged. No prediction of whether
 *  something will go, and no training advice. */
// ── REMOVED IN THE "THE RULE" RESTRUCTURE (2026-08-06) ───────────────────────
// ProjectBoard, StreakHero, LogTab and ClimbTile lived here. They are gone, not restyled:
//   · StreakHero  — the daily streak is wrong for this domain. No competitor has one, and a
//                   climber with 9 sends and a session 3 days ago was being told "No streak
//                   yet". Replaced by TopBand (rolling 12 months) in therule.tsx.
//   · LogTab/ClimbTile — the "Recent ticks" reverse-chronological card list was the shared
//                   fleet skeleton (Mood/Stash/Habits have the same shape). Replaced by
//                   Sessions, which files each tick under the session it happened in.
//   · ProjectBoard — rebuilt in therule.tsx with the session ladder.
// Full reasoning: DESIGN.md a3 evidence, restructure asks R1/R3/R5/R6.
// ── Tab 2: Insights / Season recap ────────────────────────────────────────────
function InsightsTab({ climbs }: { climbs: Climb[] }) {
  if (climbs.length === 0) {
    return (
      <div className="card" style={{ padding: "2.5rem 1.5rem", textAlign: "center" }}>
        <div style={{ fontSize: "2.6rem" }}>📈</div>
        <div className="serif" style={{ fontSize: "1.15rem", margin: "0.5rem 0 0.25rem" }}>Nothing to recap yet</div>
        <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Log a handful of climbs, then come back for your season recap.</div>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "1rem" }}>
      <SeasonRecapCard climbs={climbs} />
    </div>
  );
}

type RecapErr = { code: BrainErrorCode | "empty"; message: string };

function SeasonRecapCard({ climbs }: { climbs: Climb[] }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<SeasonSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  // The optional descriptive "ask my ticks" path (fenced pre-LLM).
  const [ask, setAsk] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [askErr, setAskErr] = useState<RecapErr | null>(null);
  const [askText, setAskText] = useState<string | null>(null);

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null);
    try {
      const sig = await store.computeSignals(todayLocal());
      const r = await recapSeason(sig);
      const recap = r.recap.trim();
      if (!recap) {
        setErr({ code: "empty", message: "Not enough logged climbs to recap yet — log a few more sessions." });
      } else {
        setText(recap);
        setSignals(sig);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  async function runAsk() {
    const q = ask.trim();
    if (!q) return;
    // PRE-LLM FENCE: refuse coaching/medical before any billed call (no spinner, no charge).
    if (isCoachingOrMedical(q)) {
      setAskErr({ code: "refused", message: "Send Log only narrates the climbs you've logged — no coaching, grade, training, or injury advice. Ask about your own ticks instead (hardest send, attempts before a tick, indoor vs outdoor)." });
      setAskText(null);
      return;
    }
    setAskBusy(true); setAskErr(null); setAskText(null);
    try {
      const sig = signals ?? await store.computeSignals(todayLocal());
      const r = await askTicks(q, sig);
      const out = r.recap.trim();
      if (!out) setAskErr({ code: "empty", message: "Couldn't find anything in your ticks for that — try another question." });
      else setAskText(out);
    } catch (e) {
      if (e instanceof BrainError) setAskErr({ code: e.code, message: e.message });
      else setAskErr({ code: "unavailable", message: "That failed — please try again." });
    } finally { setAskBusy(false); }
  }

  const askLooksCoaching = ask.trim().length > 0 && isCoachingOrMedical(ask);

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> Season recap
        </div>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", padding: "0.35rem 0.7rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy} onClick={run}>
          {busy ? "Recapping…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Recap my season"}
        </button>
      </div>

      {!busy && !text && !err && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
          Narrates your logged season — hardest send, attempts before a tick, grade drift, indoor vs outdoor. Describes your ticks, never coaching.
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} /> Reading your season…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "70%" }} />
        </div>
      )}

      {err && !busy && <RecapError err={err} onRetry={run} />}

      {text && !busy && (
        <div style={{ marginTop: "0.6rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <SignalEvidence signals={signals} />}
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 10 }}>Describes your logged climbs — not coaching, grade, training, or injury advice.</span>
          </div>
        </div>
      )}

      {/* Always-on local breakdown (no AI, deterministic) so the screen is useful before any recap. */}
      {signals && <LocalBreakdown signals={signals} />}
      {!signals && <LiveBreakdown climbs={climbs} />}

      {/* Optional descriptive "ask my ticks" — fenced pre-LLM. */}
      <div style={{ marginTop: "1rem", borderTop: "1px solid var(--border-subtle)", paddingTop: "0.85rem" }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-xs)", marginBottom: "0.45rem" }}>Ask your ticks</div>
        <form className="row" style={{ gap: "0.4rem" }} onSubmit={(e) => { e.preventDefault(); void runAsk(); }}>
          <input
            placeholder="e.g. how many V4s outdoors this year?"
            value={ask}
            onChange={(e) => { setAsk(e.target.value); if (askErr) setAskErr(null); }}
            style={{ fontSize: "var(--fs-xs)" }}
          />
          <button type="submit" className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", gap: "0.3rem", flexShrink: 0 }} disabled={askBusy || !ask.trim()}>
            <Send size={12} /> {askBusy ? "…" : "Ask"}
          </button>
        </form>
        {askLooksCoaching && !askErr && (
          <div className="muted" style={{ fontSize: 10, marginTop: 6, color: "var(--warning)" }}>
            That looks like coaching/training/injury — Send Log only narrates your logged ticks, so it won't run.
          </div>
        )}
        {askErr && <RecapError err={askErr} onRetry={runAsk} />}
        {askText && !askBusy && (
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: "0.6rem 0 0", whiteSpace: "pre-wrap" }}>{askText}</p>
        )}
      </div>
    </div>
  );
}

function RecapError({ err, onRetry }: { err: RecapErr; onRetry: () => void }) {
  if (err.code === "insufficient_funds") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "rgba(217,147,58,0.1)", border: "1px solid rgba(217,147,58,0.3)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep recapping</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then recap again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Refused (coaching/medical fence) — firm, no retry; it's a hard boundary, not a glitch.
  if (err.code === "refused") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Lock size={15} style={{ color: "var(--ink-2)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable";
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {retryable && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the actual derived numbers the recap drew from. Purely observational.
function SignalEvidence({ signals }: { signals: SeasonSignals }) {
  const chips: { label: string; title: string }[] = [];

  if (signals.hardest) {
    chips.push({
      label: `hardest ${signals.hardest.grade}`,
      title: `Hardest send this season: ${signals.hardest.grade} (${ASCENT_LABELS[signals.hardest.ascent as Ascent] ?? signals.hardest.ascent})${signals.hardest.venue ? ` at ${signals.hardest.venue}` : ""}`,
    });
  }
  const abt = signals.attempts_before_tick;
  if (abt.mean_attempts_worked !== null) {
    chips.push({
      label: `~${abt.mean_attempts_worked} tries`,
      title: `Worked sends averaged ${abt.mean_attempts_worked} attempts (${abt.worked_sends} worked, ${abt.flashed_or_onsight} flashed/onsighted)`,
    });
  }
  const gd = signals.grade_drift;
  if (gd && gd.direction !== "flat") {
    chips.push({
      label: `grade ${gd.direction === "up" ? "↑" : "↓"} ${gd.first_half_label}→${gd.second_half_label}`,
      title: `Median send grade moved ${gd.direction} across the season: ${gd.first_half_label} → ${gd.second_half_label}`,
    });
  }
  const es = signals.environment_split;
  if (es.indoor_fraction !== null && es.indoor + es.outdoor > 0) {
    chips.push({
      label: `${Math.round(es.indoor_fraction * 100)}% indoor`,
      title: `Indoor ${es.indoor} vs outdoor ${es.outdoor} this season`,
    });
  }
  if (signals.projects.length > 0) {
    const p = signals.projects[0];
    chips.push({
      label: `top project ${p.total_attempts} tries`,
      title: `Most-worked project: ${p.grade}${p.venue ? ` at ${p.venue}` : ""} — ${p.total_attempts} attempts over ${p.sessions} session${p.sessions === 1 ? "" : "s"}${p.sent ? " (sent)" : ""}`,
    });
  }

  if (chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.6rem" }}>
      {chips.map((c) => (
        <span key={c.label} className="chip tnum" title={c.title}
          style={{ padding: "0.1rem 0.45rem", fontSize: 10, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)" }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

// Deterministic on-device breakdown shown alongside the recap (the signals from Rust).
function LocalBreakdown({ signals }: { signals: SeasonSignals }) {
  const s = signals.season;
  const es = signals.environment_split;
  const indoorPct = es.indoor_fraction !== null ? Math.round(es.indoor_fraction * 100) : null;
  const ascents = signals.ascent_mix;
  const maxAscent = Math.max(1, ...ascents.map((a) => a.count));
  return (
    <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.9rem" }}>
      <div className="row tnum" style={{ gap: "1.25rem", fontSize: "var(--fs-xs)", color: "var(--ink-2)", flexWrap: "wrap" }}>
        <span><b>{s.sends}</b> sends</span>
        <span><b>{s.attempts_only}</b> attempts</span>
        <span><b>{s.distinct_days}</b> day{s.distinct_days === 1 ? "" : "s"} out</span>
        <span className="row" style={{ gap: "0.25rem" }}><MapPin size={11} /> <b>{s.distinct_venues}</b> spots</span>
        <span className="muted">last 365 days</span>
      </div>

      {indoorPct !== null && (
        <div>
          <div className="row" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)", color: "var(--ink-2)", marginBottom: 4 }}>
            <span className="row" style={{ gap: "0.3rem" }}><span className="dot" style={{ background: "var(--accent)" }} /> Indoor {es.indoor}</span>
            <span className="row" style={{ gap: "0.3rem" }}>Outdoor {es.outdoor} <span className="dot" style={{ background: "var(--send)" }} /></span>
          </div>
          <div className="split">
            <div className="split-indoor" style={{ width: `${indoorPct}%` }} />
            <div className="split-outdoor" style={{ width: `${100 - indoorPct}%` }} />
          </div>
        </div>
      )}

      {ascents.length > 0 && (
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, marginBottom: "0.45rem" }}>How you topped out</div>
          <div className="dist">
            {ascents.map((a) => (
              <div className="dist-row" key={a.key}>
                <span className="dist-label">{ASCENT_LABELS[a.key as Ascent] ?? a.key}</span>
                <div className="dist-track"><div className="dist-fill" style={{ width: `${(a.count / maxAscent) * 100}%` }} /></div>
                <span className="dist-count tnum">{a.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {signals.projects.length > 0 && (
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, marginBottom: "0.45rem" }}>Most-worked projects</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.4rem" }}>
            {signals.projects.slice(0, 4).map((p, i) => (
              <div key={`${p.grade}-${p.venue}-${i}`} className="row" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)" }}>
                <span className="row" style={{ gap: "0.4rem", minWidth: 0 }}>
                  <span className="dot project" />
                  <span style={{ fontWeight: 600 }}>{p.grade || "—"}</span>
                  <span className="muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.venue}</span>
                  {p.sent && <CheckCircle2 size={12} style={{ color: "var(--send)" }} />}
                </span>
                <span className="muted tnum">{p.total_attempts} tries · {p.sessions} sess.</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Before a recap is run we still want the Insights screen to be useful — compute the
// signals on-device and show the same breakdown (no AI, no billing).
function LiveBreakdown({ climbs }: { climbs: Climb[] }) {
  const [signals, setSignals] = useState<SeasonSignals | null>(null);
  useEffect(() => {
    let alive = true;
    void store.computeSignals(todayLocal()).then((s) => { if (alive) setSignals(s); });
    return () => { alive = false; };
  }, [climbs]);
  if (!signals) return null;
  return <LocalBreakdown signals={signals} />;
}

// ── Log / edit a climb ─────────────────────────────────────────────────────────
function LogClimb({ editing, preset, onClose, onSaved }: { editing: Climb | null; preset?: string; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  // `preset` comes from the LOG RAIL: tapping a grade band opens this sheet with that grade
  // already set, which is what makes the app's own verb the primary action (restructure R2).
  const [grade, setGrade] = useState(editing?.grade ?? preset ?? "");
  const [environment, setEnvironment] = useState<Environment>(editing?.environment ?? "indoor");
  const [discipline, setDiscipline] = useState<Discipline>(editing?.discipline ?? "boulder");
  const [ascent, setAscent] = useState<Ascent>(editing?.ascent ?? "redpoint");
  const [attempts, setAttempts] = useState<number>(editing?.attempts ?? 1);
  const [project, setProject] = useState<boolean>(editing?.project ?? false);
  const [venue, setVenue] = useState(editing?.venue ?? "");
  const [shoe, setShoe] = useState(editing?.shoe ?? "");
  const [partner, setPartner] = useState(editing?.partner ?? "");
  const [styleTags, setStyleTags] = useState<string[]>(editing ? [...editing.style] : []);
  const [customStyle, setCustomStyle] = useState("");
  const [note, setNote] = useState(editing?.note ?? "");
  const [climbedOn, setClimbedOn] = useState(editing?.climbed_on ?? todayLocal());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const sent = isSendAscent(ascent);
  const showAttempts = ascent === "redpoint" || ascent === "repeat" || ascent === "attempt";

  function toggleStyle(t: string) {
    const n = normalizeTag(t);
    setStyleTags((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));
  }
  function addCustom() {
    const n = normalizeTag(customStyle);
    if (n && !styleTags.includes(n)) setStyleTags((c) => [...c, n]);
    setCustomStyle("");
  }

  async function save() {
    if (!grade.trim()) { setErr("Add a grade (e.g. V4, 5.11a, 6c+)."); return; }
    setSaving(true); setErr(null);
    const payload: NewClimb = {
      grade: grade.trim(),
      climbed_on: climbedOn,
      environment,
      discipline,
      ascent,
      attempts: showAttempts ? Math.max(1, attempts) : 1,
      sent,
      project,
      venue: venue.trim(),
      shoe: shoe.trim(),
      partner: partner.trim(),
      style: normalizeTags(styleTags),
      note: note.trim(),
    };
    try {
      if (isEditing) {
        await store.updateClimb(editing!.id, payload);
      } else {
        await store.createClimb(payload);
      }
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(`Could not save — try again. (${msg})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit climb" : "Log climb"} onCancel={onClose} wide>
      {/* Grade + date */}
      <div className="row" style={{ gap: "0.6rem", marginBottom: "1rem", alignItems: "flex-end" }}>
        <label style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>Grade</div>
          <input placeholder="V4 · 5.11a · 6c+" value={grade} onChange={(e) => setGrade(e.target.value)} />
        </label>
        <label style={{ width: 150 }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>Date</div>
          <input type="date" value={climbedOn} onChange={(e) => setClimbedOn(e.target.value || todayLocal())} />
        </label>
      </div>

      {/* Environment */}
      <FieldLabel>Where</FieldLabel>
      <div className="row" style={{ gap: "0.4rem", marginBottom: "1rem" }}>
        {ENVIRONMENTS.map((env) => (
          <button key={env} type="button" className={`seg${environment === env ? " selected" : ""}`} onClick={() => setEnvironment(env)}>
            {ENVIRONMENT_LABELS[env]}
          </button>
        ))}
      </div>

      {/* Discipline */}
      <FieldLabel>Discipline</FieldLabel>
      <div className="row" style={{ gap: "0.4rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        {DISCIPLINES.map((d) => (
          <button key={d} type="button" className={`seg${discipline === d ? " selected" : ""}`} onClick={() => setDiscipline(d)} style={{ flex: "1 1 28%" }}>
            {DISCIPLINE_LABELS[d]}
          </button>
        ))}
      </div>

      {/* Ascent */}
      <FieldLabel>How it went</FieldLabel>
      <div className="row" style={{ gap: "0.4rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        {ASCENTS.map((a) => (
          <button key={a} type="button" className={`seg${ascent === a ? " selected" : ""}`} onClick={() => setAscent(a)} style={{ flex: "1 1 28%" }}>
            {ASCENT_LABELS[a]}
          </button>
        ))}
      </div>

      {/* Attempts (only for worked/attempt ascents) */}
      {showAttempts && (
        <div style={{ marginBottom: "1rem" }}>
          <FieldLabel>{ascent === "attempt" ? "Attempts so far" : "Attempts to send"}</FieldLabel>
          <div className="row" style={{ gap: "0.5rem" }}>
            <button type="button" className="btn" onClick={() => setAttempts((n) => Math.max(1, n - 1))} aria-label="Fewer attempts">−</button>
            <span className="tnum" style={{ minWidth: "2.5rem", textAlign: "center", fontWeight: 600 }}>{attempts}</span>
            <button type="button" className="btn" onClick={() => setAttempts((n) => n + 1)} aria-label="More attempts">+</button>
            <label className="row" style={{ marginLeft: "auto", gap: "0.4rem", fontSize: "var(--fs-xs)", width: "auto" }}>
              <input type="checkbox" checked={project} onChange={(e) => setProject(e.target.checked)} style={{ width: "auto" }} />
              <span className="row" style={{ gap: "0.25rem", color: "var(--project)" }}><Flag size={12} /> ongoing project</span>
            </label>
          </div>
        </div>
      )}

      {/* Venue + shoe + partner */}
      <div className="row" style={{ gap: "0.6rem", marginBottom: "1rem", alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ flex: "1 1 100%" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>{environment === "outdoor" ? "Crag" : "Gym"} <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></div>
          <input placeholder={environment === "outdoor" ? "e.g. Stanage" : "e.g. The Climbing Hangar"} value={venue} onChange={(e) => setVenue(e.target.value)} />
        </label>
        <label style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>Shoe <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></div>
          <input placeholder="e.g. Solution" value={shoe} onChange={(e) => setShoe(e.target.value)} />
        </label>
        <label style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.4rem" }}>Partner <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></div>
          <input placeholder="e.g. Sam" value={partner} onChange={(e) => setPartner(e.target.value)} />
        </label>
      </div>

      {/* Style tags */}
      <FieldLabel optional>Style</FieldLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
        {DEFAULT_STYLES.map((t) => (
          <button key={t} type="button" className={`chip${styleTags.includes(normalizeTag(t)) ? " selected" : ""}`} onClick={() => toggleStyle(t)}>{t}</button>
        ))}
        {styleTags.filter((t) => !DEFAULT_STYLES.map(normalizeTag).includes(t)).map((t) => (
          <button key={t} type="button" className="chip selected" onClick={() => toggleStyle(t)}>{t} <X size={11} /></button>
        ))}
      </div>
      <form className="row" style={{ gap: "0.4rem", marginBottom: "1.1rem" }} onSubmit={(e) => { e.preventDefault(); addCustom(); }}>
        <input placeholder="Add your own style…" value={customStyle} onChange={(e) => setCustomStyle(e.target.value)} style={{ fontSize: "var(--fs-xs)" }} />
        <button type="submit" className="btn" style={{ fontSize: "var(--fs-xs)" }} disabled={!customStyle.trim()}>Add</button>
      </form>

      {/* Note */}
      <FieldLabel optional>Note</FieldLabel>
      <textarea rows={2} placeholder="Beta, conditions, how it felt…" value={note} onChange={(e) => setNote(e.target.value)} style={{ marginBottom: "1rem" }} />

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={!grade.trim() || saving}>{saving ? "Saving…" : isEditing ? "Update" : "Save climb"}</button>
      </div>
    </ModalShell>
  );
}

function FieldLabel({ children, optional }: { children: React.ReactNode; optional?: boolean }) {
  return (
    <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.5rem" }}>
      {children} {optional && <span className="muted" style={{ fontWeight: 400 }}>(optional)</span>}
    </div>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(31,27,46,0.45)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      {/* fill-then-cap: `width:100%` + `maxWidth:N`. The reverse (`width:N, maxWidth:100%`) resolves
          the 100% against this item's own grid track, so the card kept its full width and spilled
          off a 402px phone (the Round Card / Cellar bug, measured in the sim). */}
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: wide ? 480 : 340, padding: "1.5rem", boxShadow: "var(--shadow-2)", borderRadius: "var(--radius-dialog)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
